import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Row } from '@tanstack/react-table'
import DataTable from 'components/DataTable/DataTable/DataTable'
import {
  DataTableProvider,
  useDataTableContext,
} from 'components/DataTable/DataTableContext'
import { cn } from 'components/UI/cn'
import { DealSearchResult, EmailThread } from 'models/emailIntegration'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { ComposeMode } from './ComposePanel'
import { EmailDealActionCallbacks } from './emailActions'
import { DealSearchSubContent } from './DealSearchSubContent'
import { InboxActionBar } from './InboxActionBar'
import {
  InboxFilterChips,
  InboxFilters,
  LockedFilters,
} from './InboxFilterChips'
import { InboxSearchBar } from './InboxSearchBar'
import { InboxTab, InboxTabs } from './InboxTabs'
import { UpdateSalesStageModal } from './UpdateSalesStageModal'
import { useInboxThreadColumns } from './useInboxThreadColumns'

/** Sync total backend row count into DataTableContext for correct bulk-select counts. */
function useSyncTotalRows(total: number) {
  const {
    methods: { setTotalRowsInBackend },
  } = useDataTableContext()

  useEffect(() => {
    setTotalRowsInBackend(total)
  }, [total, setTotalRowsInBackend])
}

/**
 * Tailwind overrides for compact email row styling.
 * The DataTable uses styled-components with hardcoded 56px rows.
 * These arbitrary selectors override to match the Figma email row spec (38px).
 *
 * Background/shadow strategy:
 *   - Target <td> directly for bg overrides — the styled-component S.TR applies
 *     `background-color: !important` on hover, which wins over TR-level Tailwind rules.
 *     Since TD uses `background-color: inherit`, setting it explicitly on TD bypasses
 *     the TR-level inheritance and always wins.
 *   - Inset box-shadow for the left bar goes on the first <td>, not <tr> — shadows on
 *     <tr> are hidden behind TD backgrounds in border-collapse: separate tables.
 */
const EMAIL_TABLE_OVERRIDES = cn(
  // Hide thead and drive column widths from TD cells instead
  '[&_thead]:!hidden',
  // Column widths: sender+checkbox(200) | subject(auto) | timestamp(130)
  '[&_td:first-child]:!w-[200px] [&_td:last-child]:!w-[130px]',
  // Compact row height (38px per Figma) — overrides DataTable's inline height:56px
  '[&_tr]:!h-[38px]',
  '[&_td]:!h-[38px] [&_td]:!max-h-[38px]',
  // Cell padding: 16px on edges, 12px between; vertical-center; reset word-break
  '[&_td]:!pl-3 [&_td:first-child]:!pl-4 [&_td:last-child]:!pr-4 [&_td]:!break-normal [&_td]:!align-middle',
  // Force CheckboxRow to match row height and center checkbox vertically
  '[&_td:first-child>div]:!h-[38px]',
  '[&_.custom-checkbox]:!flex [&_.custom-checkbox]:!items-center [&_.custom-checkbox]:!leading-none',
  // Row divider — DS2026 v2 table border (neutral-200; deepens to neutral-300 on selected)
  '[&_td]:!border-b-neutral-200',
  // Selected row — DS2026 v2 table: 15% sage fill + neutral-300 divider.
  // TD target beats the styled-component TR hover.
  '[&_tr[data-selected]_td]:!bg-surface-row-selected [&_tr[data-selected]_td]:!border-b-neutral-300',
  // Selected + hover — DS2026 v2 table: 20% sage.
  '[&_tr[data-selected]:hover_td]:!bg-surface-row-selected-hover',
  // Deal chip: gray on the default (white) row, but flip to white on any sage
  // tint — hover and selected — so the gray chip doesn't wash into the row.
  '[&_tr[data-selected]_.deal-badge]:!bg-white',
  '[&_tr:hover_.deal-badge]:!bg-white',
  // Unselected row hover — DS2026 v2 table: 5% sage fill, plus the inbox's
  // Gmail-style elevation shadow (kept on purpose). #dadce0 = line-row-separator
  // token; token names can't be used inside shadow-[...] arbitrary values.
  '[&_tr:not([data-selected]):hover_td]:!bg-surface-row-hover',
  '[&_tr:not([data-selected]):hover]:!shadow-[0px_1px_3px_1px_rgba(60,64,67,0.15),0px_1px_2px_0px_rgba(60,64,67,0.3),-1px_0px_0px_0px_#dadce0,1px_0px_0px_0px_#dadce0]',
  // Row hover: hide timestamp + attachment icon, show 3-dot menu
  '[&_tr:hover_.inbox-row-timestamp]:!invisible',
  '[&_tr:hover_.inbox-row-menu]:!visible',
  '[&_tr:hover_.inbox-row-attachment]:!invisible',
  '[&_tr]:!cursor-pointer',
  // Remove DataTable's default rounded border wrapper (inline style borderRadius:8, border:#eaecf0)
  '[&>div>div]:!rounded-none [&>div>div]:!border-0'
)

export const PAGE_SIZE = 50

interface EmailInboxViewProps {
  contactCompanyId: number
  forDoorProxyId?: number
  selectedTab: InboxTab
  onTabChange: (tab: InboxTab) => void
  onThreadClick: (
    thread: EmailThread,
    index: number,
    threads: EmailThread[],
    page: number,
    total: number,
    composeMode?: ComposeMode
  ) => void
  onCompose?: () => void
  /** Filters that are always applied and cannot be removed by the user. */
  lockedFilters?: LockedFilters
}

/**
 * Container component for the thread-based inbox list view.
 * Composes search, filters, tabs, action bar, and DataTable.
 */
export function EmailInboxView({
  contactCompanyId,
  forDoorProxyId,
  selectedTab,
  onTabChange,
  onThreadClick,
  onCompose,
  lockedFilters,
}: EmailInboxViewProps) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<InboxFilters>({})

  // Reset page when filters or tab change
  useEffect(() => {
    setPage(1)
  }, [search, filters, selectedTab])

  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      page,
      page_size: PAGE_SIZE,
      my_emails: selectedTab === 'my_emails',
    }

    // When locked deal filters are active, skip chain_proxy_id so we see ALL
    // threads assigned to the deal, not just those from one contact company.
    if (lockedFilters?.deal_ids?.length) {
      // deal_id filter is applied below — no company/door scoping needed
    } else if (forDoorProxyId) {
      params.door_proxy_id = forDoorProxyId
    } else {
      params.chain_proxy_id = contactCompanyId
    }

    if (search) {
      params.search = search
    }

    if (filters.from_emails?.length)
      params.from_email = filters.from_emails.join(',')
    if (filters.to_emails?.length) params.to_email = filters.to_emails.join(',')
    if (filters.date_greater) params.date_greater = filters.date_greater
    if (filters.date_less) params.date_less = filters.date_less
    if (filters.statuses?.length) params.status = filters.statuses.join(',')
    if (filters.has_attachment) params.has_attachment = filters.has_attachment
    if (filters.deal_assignment)
      params.deal_assignment = filters.deal_assignment
    // Merge locked + user deal_ids (locked filters take precedence)
    const allDealIds = [
      ...(lockedFilters?.deal_ids ?? []),
      ...(filters.deal_ids ?? []),
    ]
    if (allDealIds.length) params.deal_id = allDealIds.join(',')
    if (filters.show_hidden) params.show_hidden = true

    return params
  }, [
    page,
    selectedTab,
    contactCompanyId,
    forDoorProxyId,
    search,
    filters,
    lockedFilters,
  ])

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['email-threads', queryParams],
    queryFn: ({ signal }) =>
      api.emailIntegration.getEmailThreadList(queryParams, signal),
  })

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['email-threads'] })
  }, [queryClient])

  const threads = data?.results ?? []
  const total = data?.count ?? 0

  // Track thread for "Update sales stage" modal opened from row menu
  const [stageThread, setStageThread] = useState<EmailThread | null>(null)

  const removeFromDealMutation = useMutation({
    mutationFn: (threadId: number) =>
      api.emailIntegration.removeThreadFromDeal(threadId),
    onSuccess: () => {
      toast.success('Thread removed from deal')
      queryClient.invalidateQueries({ queryKey: ['email-threads'] })
    },
    onError: () => toast.error('Failed to remove thread from deal'),
  })

  const hideThreadMutation = useMutation({
    mutationFn: (threadId: number) => api.emailIntegration.hideThread(threadId),
    onSuccess: () => {
      toast.success('Thread hidden')
      queryClient.invalidateQueries({ queryKey: ['email-threads'] })
    },
    onError: () => toast.error('Failed to hide thread'),
  })

  const unhideThreadMutation = useMutation({
    mutationFn: (threadId: number) =>
      api.emailIntegration.unhideThread(threadId),
    onSuccess: () => {
      toast.success('Thread unhidden')
      queryClient.invalidateQueries({ queryKey: ['email-threads'] })
    },
    onError: () => toast.error('Failed to unhide thread'),
  })

  const assignMutation = useMutation({
    mutationFn: ({
      threadId,
      deal,
    }: {
      threadId: number
      deal: DealSearchResult
    }) =>
      api.emailIntegration.assignThreadsToDeal({
        thread_ids: [threadId],
        deal_id: deal.id,
      }),
    onSuccess: () => {
      toast.success('Thread assigned to deal')
      queryClient.invalidateQueries({ queryKey: ['email-threads'] })
    },
    onError: () => toast.error('Failed to assign thread to deal'),
  })

  /** Navigate to thread detail, optionally opening a compose panel. */
  const navigateToThread = useCallback(
    (thread: EmailThread, composeMode?: ComposeMode) => {
      const idx = threads.findIndex((t) => t.id === thread.id)
      onThreadClick(
        thread,
        idx >= 0 ? idx : 0,
        threads,
        page,
        total,
        composeMode
      )
    },
    [threads, page, total, onThreadClick]
  )

  const handleViewThread = useCallback(
    (thread: EmailThread) => navigateToThread(thread),
    [navigateToThread]
  )

  const handleComposeThread = useCallback(
    (thread: EmailThread, mode: ComposeMode) => navigateToThread(thread, mode),
    [navigateToThread]
  )

  /** Build deal action callbacks per thread for the row menu. */
  const getDealCallbacks = useCallback(
    (thread: EmailThread): EmailDealActionCallbacks => ({
      dealId: thread.deal_id,
      onAssignToDeal: !thread.deal_id ? () => {} : undefined,
      onRemoveFromDeal: thread.deal_id
        ? () => removeFromDealMutation.mutate(thread.id)
        : undefined,
      onUpdateSalesStage: thread.deal_id
        ? () => setStageThread(thread)
        : undefined,
      // Create deal / Schedule reminder only in detail view
      onCreateDeal: undefined,
      onScheduleReminder: undefined,
      onHideThread: !thread.is_hidden
        ? () => hideThreadMutation.mutate(thread.id)
        : undefined,
      onUnhideThread: thread.is_hidden
        ? () => unhideThreadMutation.mutate(thread.id)
        : undefined,
    }),
    [removeFromDealMutation, hideThreadMutation, unhideThreadMutation]
  )

  /** Build assign-deal submenu content per thread. */
  const getAssignDealSubContent = useCallback(
    (thread: EmailThread): React.ReactNode =>
      !thread.deal_id ? (
        <DealSearchSubContent
          contactCompanyId={contactCompanyId}
          onAssign={(deal) =>
            assignMutation.mutate({ threadId: thread.id, deal })
          }
        />
      ) : undefined,
    [contactCompanyId, assignMutation]
  )

  const columns = useInboxThreadColumns({
    onViewThread: handleViewThread,
    onComposeThread: handleComposeThread,
    getDealCallbacks,
    getAssignDealSubContent,
  })

  const handleRowClick = useCallback(
    (row: Row<EmailThread>) => {
      onThreadClick(row.original, row.index, threads, page, total)
    },
    [onThreadClick, threads, page, total]
  )

  const tableKey = forDoorProxyId
    ? `email-threads-door-${forDoorProxyId}`
    : `email-threads-company-${contactCompanyId}`

  return (
    <div className="h-full flex flex-col gap-3">
      <InboxSearchBar
        value={search}
        onChange={setSearch}
        onCompose={onCompose}
      />
      <InboxFilterChips
        contactCompanyId={contactCompanyId}
        filters={filters}
        onChange={setFilters}
        lockedFilters={lockedFilters}
      />
      <ColumnsStoreProvider tableKey="EMAIL_THREADS_TABLE">
        <DataTableProvider tableKey={tableKey}>
          <InboxTableContent
            contactCompanyId={contactCompanyId}
            page={page}
            total={total}
            threads={threads}
            columns={columns}
            tableKey={tableKey}
            isLoading={isLoading}
            selectedTab={selectedTab}
            onPageChange={setPage}
            onTabChange={onTabChange}
            isRefreshing={isFetching && !isLoading}
            onRefresh={handleRefresh}
            onRowClick={handleRowClick}
          />
        </DataTableProvider>
      </ColumnsStoreProvider>

      {/* Update sales stage modal — opened from row menu */}
      {stageThread?.deal_id && (
        <UpdateSalesStageModal
          dealId={stageThread.deal_id}
          dealIdentifier={stageThread.deal_identifier ?? null}
          open={!!stageThread}
          onClose={() => setStageThread(null)}
        />
      )}
    </div>
  )
}

/** Inner content rendered inside DataTableProvider so it can access context. */
function InboxTableContent({
  contactCompanyId,
  page,
  total,
  threads,
  columns,
  tableKey,
  isLoading,
  isRefreshing,
  selectedTab,
  onPageChange,
  onTabChange,
  onRefresh,
  onRowClick,
}: {
  contactCompanyId: number
  page: number
  total: number
  threads: EmailThread[]
  columns: ReturnType<typeof useInboxThreadColumns>
  tableKey: string
  isLoading: boolean
  isRefreshing: boolean
  selectedTab: InboxTab
  onPageChange: (page: number) => void
  onTabChange: (tab: InboxTab) => void
  onRefresh: () => void
  onRowClick: (row: Row<EmailThread>) => void
}) {
  useSyncTotalRows(total)

  return (
    <div
      className={cn(
        'flex-1 min-h-0 flex flex-col rounded-xl border border-neutral-200 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.03)] overflow-hidden'
      )}
    >
      <InboxActionBar
        contactCompanyId={contactCompanyId}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        threads={threads}
        isRefreshing={isRefreshing}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
      />
      <InboxTabs
        selectedTab={selectedTab}
        onTabChange={onTabChange}
        counts={{ [selectedTab]: total }}
      />
      <div
        className={cn('flex-1 min-h-0 overflow-auto', EMAIL_TABLE_OVERRIDES)}
      >
        <DataTable<EmailThread>
          columns={columns}
          data={threads}
          tableKey={tableKey}
          loading={isLoading}
          virtualizeRows={false}
          isPaginationEnabled={false}
          enableRowSelection={true}
          onRowClick={onRowClick}
          emptyTableText={{
            title: 'No email threads found',
            subtitle: 'Try adjusting your filters or search terms',
          }}
        />
      </div>
    </div>
  )
}
